import { bbGenerateShortId } from 'app/shared/utils/bbGenerateShortId.util';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { QueryParameter } from './../../../interfaces/queryString';
import { FilterSet } from './../../../services/filter-set.service';
import { SapiService, SapiFacetResponse, SapiQueryResponse } from './../../../services/sapi.service';
import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { NgForm } from '@angular/forms';
import { AdSchedule } from './../../../interfaces/adschedule';
import { TranslateService } from '@ngx-translate/core';
import { CdkDragSortEvent, moveItemInArray } from '@angular/cdk/drag-drop';

@Component({
	selector: 'adschedule-details-metadata',
	templateUrl: './adschedule-details-metadata.component.html',
	styleUrls: ['./adschedule-details-metadata.component.scss']
})

export class AdScheduleDetailsMetadataComponent implements OnInit, OnChanges {
	@Input() adschedule: AdSchedule;
	@Input() form: NgForm;

	adPositions: {
		name: string;
		title: string;
		description: string;
		open: boolean;
	}[];

	assignedPlayouts: string[] = [];
	playoutFilterSet: FilterSet = [{
		filters: [{
			type: 'playout',
			field: 'label',
			operator: 'doesNotContainAnyOf',
			value: []
		}]
	}];
	assignedAuctionIds = {
		preroll: [],
		midroll: [],
		postroll: []
	};

	private titleValueUpdated: Subject<string> = new Subject<string>();

	constructor(private sapiService: SapiService, private translate: TranslateService) {
		this.titleValueUpdated
			.pipe(debounceTime(300), distinctUntilChanged())
			.subscribe(value => this.updateCode(value));
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.adschedule && this.adschedule) {
			this.updateAssignedAuctions();
		}
	}

	ngOnInit() {
		this.adPositions = [{
			name: 'preroll',
			title: this.translate.instant('PREROLL'),
			description: this.translate.instant('PREROLL_DESCRIPTION'),
			open: false
		},
		{
			name: 'midroll',
			title: this.translate.instant('MIDROLL'),
			description: this.translate.instant('MIDROLL_DESCRIPTION'),
			open: false
		},
		{
			name: 'postroll',
			title: this.translate.instant('POSTROLL'),
			description: this.translate.instant('POSTROLL_DESCRIPTION'),
			open: false
		}];
	}

	auctionSelected(positionName, auction) {
		this.adschedule[positionName].items.push({
			id: bbGenerateShortId(),
			auction_id: auction.id
		});
		this.updateAssignedAuctions();
		this.form.form.markAsDirty();
	}

	updateAssignedAuctions() {
		this.assignedAuctionIds = {
			preroll: this.adschedule?.preroll?.items?.filter(item => item.auction_id).map(item => item.auction_id) || [],
			midroll: this.adschedule?.midroll?.items?.filter(item => item.auction_id).map(item => item.auction_id) || [],
			postroll: this.adschedule?.postroll?.items?.filter(item => item.auction_id).map(item => item.auction_id) || []
		};
	}

	excludeTargetedPlayouts() {
		this.sapiService.getFacetOptions('adschedule', 'playoutCodes', 9999).subscribe((response: SapiFacetResponse) => {
			if (response?.facetList?.length) {
				this.playoutFilterSet[0].filters[0].value = response.facetList;
			}
		});
	}

	removePositionItem(positionName: string, index: number): void {
		this.adschedule[positionName].items.splice(index, 1);
		this.updateAssignedAuctions();
		this.form.form.markAsDirty();
	}

	addVastUrl(positionName: string): void {
		this.adschedule[positionName].items.push({
			id: bbGenerateShortId(),
			vast_url: ''
		});
		this.form.form.markAsDirty();
	}

	onVastItemDropped(ev: CdkDragSortEvent, positionName) {
		moveItemInArray(this.adschedule[positionName].items, ev.previousIndex, ev.currentIndex);
		this.form.form.markAsDirty();
	}

	trackByIndex(index: number, item: any) {
		return item?.id || index;
	}

	// for new ad schedules: auto create code based on title
	onTitleUpdated(title: string) {
		if (title && !this.adschedule.id) {
			this.titleValueUpdated.next(title);
		}
	}

	updateCode(value: string) {
		if (this.adschedule.id) {
			return;
		}
		this.adschedule.code = value ? value.replace(/-/g, '*').replace(/[^\w\-]{1,}/g, '_').toLowerCase() : '';

		const queryParams: QueryParameter[] = [{ name: 'q', values: ['code:' + this.adschedule.code] }];

		this.sapiService.query('adschedule', queryParams).subscribe((sapiQueryResponse: SapiQueryResponse) => {
			if (sapiQueryResponse && sapiQueryResponse.totalAmount > 0) {
				this.form.form.controls.code.setErrors({ 'not-unique': true });
				this.form.form.controls.code.markAsTouched();
				this.form.form.controls.code.markAsDirty();
			}
		});
	}

	/**
	 * Set the playouts assigned to this ad schedule and update the exclude list from Solr
	 *
	 * @param Array<string> assignedPlayoutCodes - Array of playout code strings that are assigned to the ad schedule
	 **/
	setAssignedPlayouts(assignedPlayoutCodes: string[] = []): void {
		this.assignedPlayouts = assignedPlayoutCodes;
		this.excludeTargetedPlayouts();
	}

	/**
	 * Updates the list of playout labels to exclude when assigning
	 *
	 * @param Array<string> diff - Array of playout code strings that are assigned to the ad schedule on save
	 **/
	updateExcludeTargetedPlayoutsFilterSet(diff: string[]): void {
		const previouslyAssignedPlayouts = this.assignedPlayouts;

		const unassigned = previouslyAssignedPlayouts.filter(playoutCode => !diff.includes(playoutCode));
		const assigned = diff.filter(playoutCode => !previouslyAssignedPlayouts.includes(playoutCode));

		this.playoutFilterSet[0].filters[0].value = (this.playoutFilterSet[0].filters[0].value as string[]).concat(assigned).filter(playoutCode => !unassigned.includes(playoutCode));
		this.assignedPlayouts = diff;
	}
}
